import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getUserId } from '../utils/userUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faUserShield, faUserPlus, faBuilding, faShieldAlt, faSatelliteDish, faDownload, faClock } from '@fortawesome/free-solid-svg-icons';
import { isSuperAdmin } from '../utils/permissions';
import UserManagement from './admin/UserManagement';
import RoleManagement from './admin/RoleManagement';
// Domain Management removed
import AllowedUserManagement from './admin/AllowedUserManagement';
import AccountManagement from './admin/AccountManagement';
import PermissionManagement from './admin/PermissionManagement';
import ChannelManagement from './admin/ChannelManagement';
import CronJobsManagement from './admin/CronJobsManagement';
import DataExport from './admin/DataExport';
import './AdminPage.css';

function AdminPage({ user, onImpersonateUser }) {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('users');
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Add a ref to track the debounce timeout
  const debounceTimeoutRef = useRef(null);

  // Create a debounced version of setActiveTab to prevent rapid consecutive renders
  const debouncedSetActiveTab = useCallback((tab) => {
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set a new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      setActiveTab(tab);
    }, 50);
  }, []);

  // Clean up the debounce timeout when the component unmounts
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Check if user is admin
    if (!user || (user.role_name !== 'Admin' && user.role_name !== 'Super Admin' &&
                 user.role !== 'Admin' && user.role !== 'Super Admin')) {
      setError('Access denied. Admin privileges required.');
      return;
    }

    // Check for tab in URL query params
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    const validTabs = ['users', 'roles', 'allowed-users', 'accounts', 'permissions', 'channels', 'cron-jobs', 'export'];

    if (tabParam && validTabs.includes(tabParam)) {
      setActiveTab(tabParam);
    } else {
      // Set accounts as the default tab
      setActiveTab('accounts');
    }
  }, [user, location.search]);

  // Navigation is now handled by the Layout component

  // Function to trigger refresh for all admin tables - memoized to prevent unnecessary re-creation
  const refreshAllTables = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Memoize the renderTabContent function to prevent unnecessary re-creation
  const renderTabContent = useCallback(() => {
    if (error) {
      return (
        <div className="admin-page-content loading-container">
          <div className="loading-content">
            <p>{error}</p>
            <button className="retry-button" onClick={() => window.location.reload()}>Retry</button>
          </div>
        </div>
      );
    }

    // If a regular Admin tries to access a Super Admin-only tab, redirect to
    // accounts. Covers ?tab=… deep links, which bypass the hidden tab buttons.
    if (!isSuperAdmin(user) &&
        (activeTab === 'roles' || activeTab === 'permissions' || activeTab === 'data-export'
          || activeTab === 'cron-jobs' || activeTab === 'channels')) {
      // Redirect to accounts tab
      setActiveTab('accounts');
      return (
        <div className="admin-page-content loading-container">
          <div className="loading-content">
            <p>Access denied. Super Admin privileges required for this section.</p>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'accounts':
        return <AccountManagement userId={getUserId()} refreshTrigger={refreshTrigger} onRefresh={refreshAllTables} />;
      case 'users':
        return <UserManagement userId={getUserId()} currentUser={user} onImpersonateUser={onImpersonateUser} refreshTrigger={refreshTrigger} onRefresh={refreshAllTables} />;
      case 'roles':
        return <RoleManagement userId={getUserId()} refreshTrigger={refreshTrigger} onRefresh={refreshAllTables} />;
      case 'permissions':
        return <PermissionManagement userId={getUserId()} refreshTrigger={refreshTrigger} onRefresh={refreshAllTables} />;
      // Domain Management case removed
      case 'allowed-users':
        return <AllowedUserManagement userId={getUserId()} refreshTrigger={refreshTrigger} onRefresh={refreshAllTables} />;
      case 'channels':
        return <ChannelManagement userId={getUserId()} refreshTrigger={refreshTrigger} onRefresh={refreshAllTables} />;
      case 'cron-jobs':
        return <CronJobsManagement userId={getUserId()} refreshTrigger={refreshTrigger} />;
      case 'data-export':
        return <DataExport userId={getUserId()} refreshTrigger={refreshTrigger} onRefresh={refreshAllTables} />;
      default:
        return <AccountManagement userId={getUserId()} refreshTrigger={refreshTrigger} onRefresh={refreshAllTables} />;
    }
  }, [activeTab, error, user, refreshTrigger, onImpersonateUser]);

  return (
    <div className="admin-page-container">
      <h1 className="admin-page-title">Admin Dashboard</h1>

          <div className="admin-tabs">
            {/* Accounts tab - available to all admins */}
            <button
              className={`admin-tab ${activeTab === 'accounts' ? 'active' : ''}`}
              onClick={() => debouncedSetActiveTab('accounts')}
            >
              <FontAwesomeIcon icon={faBuilding} className="tab-icon" />
              Accounts
            </button>

            {/* Users tab - available to all admins */}
            <button
              className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => debouncedSetActiveTab('users')}
            >
              <FontAwesomeIcon icon={faUsers} className="tab-icon" />
              Users
            </button>

            {/* Roles tab - only available to Super Admins */}
            {isSuperAdmin(user) && (
              <button
                className={`admin-tab ${activeTab === 'roles' ? 'active' : ''}`}
                onClick={() => debouncedSetActiveTab('roles')}
              >
                <FontAwesomeIcon icon={faUserShield} className="tab-icon" />
                Roles
              </button>
            )}

            {/* Permissions tab - only available to Super Admins */}
            {isSuperAdmin(user) && (
              <button
                className={`admin-tab ${activeTab === 'permissions' ? 'active' : ''}`}
                onClick={() => debouncedSetActiveTab('permissions')}
              >
                <FontAwesomeIcon icon={faShieldAlt} className="tab-icon" />
                Permissions
              </button>
            )}

            {/* Allowed Users tab - available to all admins */}
            <button
              className={`admin-tab ${activeTab === 'allowed-users' ? 'active' : ''}`}
              onClick={() => debouncedSetActiveTab('allowed-users')}
            >
              <FontAwesomeIcon icon={faUserPlus} className="tab-icon" />
              Allowed Users
            </button>

            {/* Channel Config tab - only available to Super Admins. Channel
                configs hold platform API credentials and their account
                assignments, so account-level Admins don't manage them. */}
            {isSuperAdmin(user) && (
              <button
                className={`admin-tab ${activeTab === 'channels' ? 'active' : ''}`}
                onClick={() => debouncedSetActiveTab('channels')}
              >
                <FontAwesomeIcon icon={faSatelliteDish} className="tab-icon" />
                Channel Config
              </button>
            )}

            {/* Cron Jobs tab - only available to Super Admins */}
            {isSuperAdmin(user) && (
              <button
                className={`admin-tab ${activeTab === 'cron-jobs' ? 'active' : ''}`}
                onClick={() => debouncedSetActiveTab('cron-jobs')}
              >
                <FontAwesomeIcon icon={faClock} className="tab-icon" />
                Cron Jobs
              </button>
            )}

            {/* Data Export tab - only available to Super Admins */}
            {isSuperAdmin(user) && (
              <button
                className={`admin-tab ${activeTab === 'data-export' ? 'active' : ''}`}
                onClick={() => debouncedSetActiveTab('data-export')}
              >
                <FontAwesomeIcon icon={faDownload} className="tab-icon" />
                Data Export
              </button>
            )}
          </div>

          <div className="admin-content">
            {renderTabContent()}
          </div>
        </div>
  );
}

export default AdminPage;
